import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BudgetControlCardFilter from "./BudgetControlCardFilter";
import BudgetControlCardItem from "./BudgetControlCardItem";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import { DEFAULT_BUDGET_SORT, sortBudgetRows } from "../../helpers/budgetSort";
import { setBrandNameForBudget } from "../../../../../../../../../features/bizDevelopmentDashboardSlice";

const cleanNumber = (value: any): number =>
  parseFloat(String(value ?? "0").replace(/[% ,]/g, "")) || 0;

// Budget overuse — any of these three ≥100% qualifies a row (mirrors Desktop table)
// base solely on perc_of_budget_used>=100 (https://optival.atlassian.net/browse/GR-21152)
const budgetOveruseKeys = [
  "perc_of_budget_used",
  // "perc_of_qftd_budget_used",
  // "perc_of_qftd_RR_budget_use",
];

const BudgetControlCard = () => {
  const budgetData: any[] = useSelector(
    (state: any) =>
      state.bizDevelopmentDashboardMobile.budgetMobileDataOfBizDevelopment ??
      [],
  );

  const { fetchBudgetData } = useBizDevelopmentDashboardMobile();

  const [overuse, setOveruse] = useState(false);
  const [sortKey, setSortKey] = useState(DEFAULT_BUDGET_SORT.key);
  const [sortDir, setSortDir] = useState<"asc" | "desc">(
    DEFAULT_BUDGET_SORT.dir,
  );
  const dispatch = useDispatch();
  // Fetched brand name from Redux
  const brandNameForBudget = useSelector(
    (state: any) => state.bizDevelopmentDashboard.brandNameForBudget,
  );
  // mobile metadata for dynamic CSV columns
  const budgetMetaData =
    useSelector(
      (state: any) =>
        state.bizDevelopmentDashboardMobile
          .budgetMobileMetaDataOfBizDevelopment,
    ) ?? {};

  // Unique brand names for the search Autocomplete
  const brandNames = useMemo(() => {
    const set = new Set<string>();
    (budgetData || []).forEach((r: any) => {
      if (typeof r?.brand_display_name === "string")
        set.add(r.brand_display_name.trim());
    });
    return Array.from(set);
  }, [budgetData]);

  // Brand name filter + overuse filter
  const filteredData = useMemo(() => {
    const search = (brandNameForBudget ?? "").trim().toLowerCase();
    let data = budgetData;

    if (search) {
      data = data.filter((row: any) =>
        String(row.brand_display_name ?? "")
          .toLowerCase()
          .includes(search),
      );
    }

    if (overuse) {
      data = data.filter((row: any) =>
        budgetOveruseKeys.some((key) => cleanNumber(row[key]) * 100 >= 100),
      );
    }

    return data;
  }, [budgetData, overuse, brandNameForBudget]);

  // Card view supports sorting by all metrics
  const sortedData = useMemo(
    () => sortBudgetRows(filteredData, sortKey, sortDir),
    [filteredData, sortKey, sortDir],
  );

  const handleOveruseToggle = (checked: boolean) => {
    setOveruse(checked);
  };

  const handleApplySort = (key: string, dir: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(dir);
  };

  // Refresh — re-fetch budget data (mirrors Desktop)
  const handleRefresh = () => {
    fetchBudgetData();
  };

  const handleDownloadCSV = () => {
    if (sortedData.length === 0) return;

    const toPercent = (value: any): number => {
      if (value === null || value === undefined) return 0;
      return (parseFloat(String(value).replace(/[% ,]/g, "")) || 0) * 100;
    };

    const formatNumber = (value: any): string => {
      const num = parseFloat(String(value).replace(/[, ]/g, ""));
      if (isNaN(num)) return String(value ?? "");
      return Math.abs(num).toLocaleString("en-US", {
        maximumFractionDigits: 0,
      });
    };

    const percentKeys = new Set([
      "perc_of_budget_used",
      "perc_of_qftd_RR_budget_use",
      "perc_of_qftd_budget_used",
    ]);

    const currencyKeys = new Set([
      "cpa_constraint",
      "total_income",
      "cpa_income",
    ]);

    // Dynamic columns from metadata (mirrors desktop ordering/titles)
    const orderedColumns = Object.entries(budgetMetaData)
      .filter(([key]) => key !== "total_income_constraint")
      .sort(([, a]: any, [, b]: any) => Number(a.order) - Number(b.order))
      .map(([key, meta]: any) => {
        let title = (meta.title as string).replace(/^Perc Of\s*/i, "% ");
        if (key === "cpa_constraint") title = "CPA Budget";
        if (key === "perc_of_qftd_RR_budget_use") title = "% of RR Budget Used";
        if (key === "perc_of_qftd_budget_used") title = "% QFTD Used";
        return { key, title };
      });

    const headers = orderedColumns.map((col) => col.title).join(",");

    const escapeCell = (value: any): string => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      return str.includes(",") || str.includes('"') || str.includes("\n")
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };

    const formatCell = (key: string, value: any): string => {
      if (value === null || value === undefined || value === "") return "";

      if (percentKeys.has(key)) {
        return `${toPercent(value).toFixed(1)}%`;
      }

      if (currencyKeys.has(key)) {
        const num = parseFloat(String(value).replace(/[, ]/g, ""));
        if (isNaN(num)) return "";
        return num < 0 ? `-$${formatNumber(value)}` : `$${formatNumber(value)}`;
      }

      // Remaining QFTD — truncated toward zero (matches UI Math.trunc)
      if (key === "remaining_qftd") {
        const num = Number(String(value).replace(/[, ]/g, ""));
        return Number.isFinite(num) ? String(Math.trunc(num)) : "";
      }

      // QFTD Budget — floored, $ prefixed, thousands-separated (matches UI roundDownBudget)
      if (key === "qftd_budget") {
        const num = Number(String(value).replace(/[, ]/g, ""));
        return Number.isFinite(num)
          ? `$${Math.floor(num).toLocaleString("en-US")}`
          : "";
      }

      return escapeCell(value);
    };

    const csvRows = sortedData.map((row: any) =>
      orderedColumns
        .map((col) => escapeCell(formatCell(col.key, row[col.key])))
        .join(","),
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `budget_control_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <BudgetControlCardFilter
        brandCount={filteredData.length}
        overuse={overuse}
        onOveruseToggle={handleOveruseToggle}
        onRefresh={handleRefresh}
        onDownload={handleDownloadCSV}
        sortKey={sortKey}
        sortDir={sortDir}
        onApplySort={handleApplySort}
        brandList={brandNames}
        brandName={brandNameForBudget}
        onBrandNameChange={(value) => dispatch(setBrandNameForBudget(value))}
      />
      <BudgetControlCardItem data={sortedData} />
    </>
  );
};

export default BudgetControlCard;
